import { Button } from "./ui/Button";
import { isOverdue } from "./TaskCard";
import { daysUntil, dueLabel, shortDate } from "../lib/dates";
import { FALLBACK_COLOR } from "../lib/palette";
import { mindmapUrl } from "../lib/links";
import type { Project, Task } from "../types";

interface Props {
  projects: Project[];
  tasks: Task[];
  projectById: Map<string, Project>;
  onEnter: (projectId: string) => void;
  onOpenTask: (task: Task) => void;
  onArchive: (project: Project) => void;
  onRestore: (project: Project) => void;
  onEdit: (project: Project) => void;
  onMindmap: (project: Project) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
}

export function HomeView({
  projects,
  tasks,
  projectById,
  onEnter,
  onOpenTask,
  onArchive,
  onRestore,
  onEdit,
  onMindmap,
  showArchived,
  onToggleArchived,
}: Props) {
  const active = projects.filter((p) => !p.archived);
  const archived = projects.filter((p) => p.archived);

  const attention = tasks
    .filter((t) => {
      if (t.status === "Done" || !t.deadline) return false;
      const project = projectById.get(t.projectId);
      if (!project || project.archived) return false;
      const days = daysUntil(t.deadline);
      return days !== null && days <= 7;
    })
    .sort((a, b) => a.deadline.localeCompare(b.deadline));

  return (
    <>
      {attention.length > 0 && (
        <div className="strip">
          <div className="strip__head">Needs attention</div>
          {attention.map((task) => {
            const project = projectById.get(task.projectId);
            return (
              <button
                key={task.id}
                className="strip__row"
                onClick={() => onOpenTask(task)}
              >
                <span
                  className="dot"
                  style={{ background: project?.color ?? FALLBACK_COLOR }}
                />
                <span className="strip__name">{task.title}</span>
                <span className="strip__proj">{project?.name ?? ""}</span>
                <span
                  className={`strip__due${isOverdue(task) ? " strip__due--late" : ""}`}
                >
                  {dueLabel(task.deadline)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {active.length > 0 ? (
        <div className="grid">
          {active.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              tasks={tasks.filter((t) => t.projectId === p.id)}
              onEnter={onEnter}
              onArchive={onArchive}
              onRestore={onRestore}
              onEdit={onEdit}
              onMindmap={onMindmap}
            />
          ))}
        </div>
      ) : (
        <p className="empty">No active projects. Create one to start adding tasks.</p>
      )}

      {archived.length > 0 && (
        <>
          <div className="archline">
            <Button variant="quiet" onClick={onToggleArchived}>
              {showArchived ? "Hide" : "Show"} archived ({archived.length})
            </Button>
          </div>
          {showArchived && (
            <>
              <h2 className="archhead">Archived</h2>
              <div className="grid">
                {archived.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    tasks={tasks.filter((t) => t.projectId === p.id)}
                    onEnter={onEnter}
                    onArchive={onArchive}
                    onRestore={onRestore}
                    onEdit={onEdit}
                    onMindmap={onMindmap}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

interface CardProps {
  project: Project;
  tasks: Task[];
  onEnter: (projectId: string) => void;
  onArchive: (project: Project) => void;
  onRestore: (project: Project) => void;
  onEdit: (project: Project) => void;
  onMindmap: (project: Project) => void;
}

function ProjectCard({
  project,
  tasks,
  onEnter,
  onArchive,
  onRestore,
  onEdit,
  onMindmap,
}: CardProps) {
  const count = (status: string) => tasks.filter((t) => t.status === status).length;
  const late = tasks.filter(isOverdue).length;
  const next = tasks
    .filter((t) => t.status !== "Done" && t.deadline)
    .sort((a, b) => a.deadline.localeCompare(b.deadline))[0];

  return (
    <div className={`pcard${project.archived ? " pcard--archived" : ""}`}>
      <button className="pcard__head" onClick={() => onEnter(project.id)}>
        <span className="pcard__swatch" style={{ background: project.color }} />
        <span className="pcard__name">{project.name}</span>
      </button>
      <div className="pcard__stats">
        <div>
          {count("Not Started")} not started &middot; {count("In Progress")} in
          progress
        </div>
        <div>
          {count("Stuck")} stuck &middot; {count("Done")} done
        </div>
        <div>
          {project.archived ? (
            "Archived"
          ) : late ? (
            <span className="pcard__late">{late} overdue</span>
          ) : next ? (
            `Next due ${shortDate(next.deadline)}`
          ) : (
            "No deadlines set"
          )}
        </div>
      </div>
      <div className="pcard__acts">
        <Button onClick={() => onEdit(project)}>Edit</Button>
        <Button
          onClick={() => onMindmap(project)}
          disabled={!mindmapUrl(project.mindmap)}
          title={
            mindmapUrl(project.mindmap) ?? "No mindmap set. Add one under Edit."
          }
        >
          Mindmap
        </Button>
        {project.archived ? (
          <Button onClick={() => onRestore(project)}>Restore</Button>
        ) : (
          <Button onClick={() => onArchive(project)}>Archive</Button>
        )}
      </div>
    </div>
  );
}
